import React, { useState, useEffect, useCallback, useMemo } from "react";
import { fetchUnifiedSearch, EntityType } from "../PhotoManager/API/api";
import { getEncodedEntityPath } from "../PhotoManager/components/FolderFeatures/EncryptionOfFolder";
import TagResults from "./components/TagResults";
import ContentResults from "./components/ContentResults";
import GroupResults from "./components/GroupResults";
import PeopleResults from "./components/PeopleResults";
import FilteredPhotosPage from "./FilteredPhotosPage";
import { UnifiedSearchResult, LibraryEntity, TabType } from "./types";

const geoLoader = require("../../assets/gss-loader.gif");

interface UnifiedSearchResultsProps {
  initialQuery?: string;
  selfieSearchS3Bucket?: string;
  currentUserId?: number;
  redactionCheckEnabled?: boolean;
  canGenerateShowreels?: boolean;
  organizationId?: number;
}

interface FilteredView {
  entityType: EntityType;
  entityId: number;
  entityName: string;
}

// Runtime validation helper for EntityType
const VALID_ENTITY_TYPES: readonly EntityType[] = [
  "tag",
  "person",
  "group",
  "event",
  "album",
  "folder"
] as const;

const isValidEntityType = (value: unknown): value is EntityType => {
  return (
    typeof value === "string" &&
    VALID_ENTITY_TYPES.includes(value as EntityType)
  );
};

const parseFilteredView = (
  entityTypeParam: string | null,
  entityId: string | null,
  entityName: string | null
): FilteredView | null => {
  if (!entityTypeParam || !entityId || !entityName) {
    return null;
  }

  if (!isValidEntityType(entityTypeParam)) {
    console.warn(
      `Invalid entity_type in URL: "${entityTypeParam}". Expected one of: ${VALID_ENTITY_TYPES.join(
        ", "
      )}`
    );
    return null;
  }

  const parsedId = parseInt(entityId, 10);
  if (!Number.isFinite(parsedId) || parsedId <= 0) {
    return null;
  }

  return {
    entityType: entityTypeParam,
    entityId: parsedId,
    entityName
  };
};

const parseFilteredViewFromURL = (): FilteredView | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const selfieImage = urlParams.get("selfie_image");

  if (selfieImage) {
    return {
      entityType: "album",
      entityId: -1,
      entityName: "Selfie Search Results"
    };
  }

  const entityTypeParam = urlParams.get("entity_type");
  const entityId = urlParams.get("entity_id");
  const entityName = urlParams.get("entity_name");

  return parseFilteredView(entityTypeParam, entityId, entityName);
};

const UnifiedSearchResults: React.FC<UnifiedSearchResultsProps> = ({
  initialQuery = "",
  selfieSearchS3Bucket = "geosnapshot-selfie-search",
  currentUserId,
  redactionCheckEnabled,
  canGenerateShowreels = false,
  organizationId
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] =
    useState<UnifiedSearchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [filteredView, setFilteredView] = useState<FilteredView | null>(null);

  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    try {
      const results = await fetchUnifiedSearch(searchQuery, {
        page: 1,
        limit: 20,
        showAll: true
      });
      setSearchResults(results);
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlQuery = urlParams.get("q") || initialQuery;
    const tabParam = urlParams.get("tab") as TabType;

    const parsedView = parseFilteredViewFromURL();
    setFilteredView(parsedView);

    if (!parsedView) {
      if (
        tabParam &&
        ["all", "tags", "content", "groups", "people"].includes(tabParam)
      ) {
        setActiveTab(tabParam);
      }

      if (urlQuery) {
        setQuery(urlQuery);
        performSearch(urlQuery);
      }
    }
  }, [initialQuery, performSearch]);

  const handleBack = useCallback(() => {
    if (window.history.length <= 1) {
      window.location.href = '/library';
    } else {
      window.history.back();
    }
  }, []);

  const handleFilteredViewClick = useCallback(
    (entityType: EntityType, entityId: number, entityName: string) => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("entity_type", entityType);
      urlParams.set("entity_id", entityId.toString());
      urlParams.set("entity_name", entityName);

      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.pushState({}, "", newUrl);

      setFilteredView({
        entityType,
        entityId,
        entityName
      });
    },
    []
  );

  useEffect(() => {
    const handlePopState = () => {
      const parsedView = parseFilteredViewFromURL();
      setFilteredView(parsedView);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const generateEntityURL = useCallback((entity: LibraryEntity): string => {
    const { type, id } = entity;
    const prefix = type === "event" ? "g" : type === "album" ? "a" : "f";
    const path = `${prefix}:${id}`;
    const encodedPath = getEncodedEntityPath(path);
    return `/library?p=${encodedPath}`;
  }, []);

  const handleEntityClick = useCallback(
    (entity: LibraryEntity) => {
      window.location.href = generateEntityURL(entity);
    },
    [generateEntityURL]
  );

  const getTotalCount = useCallback(() => {
    if (!searchResults?.meta.countsByType) return 0;
    const { tags, libraryEntities, groups, people } =
      searchResults.meta.countsByType;
    return tags + libraryEntities + groups + people;
  }, [searchResults]);

  const getTabCount = useCallback(
    (tab: TabType): number => {
      if (!searchResults?.meta.countsByType) return 0;
      switch (tab) {
        case "all":
          return getTotalCount();
        case "tags":
          return searchResults.meta.countsByType.tags;
        case "content":
          return searchResults.meta.countsByType.libraryEntities;
        case "groups":
          return searchResults.meta.countsByType.groups;
        case "people":
          return searchResults.meta.countsByType.people;
        default:
          return 0;
      }
    },
    [searchResults, getTotalCount]
  );

  const highlightSearchTerm = useCallback(
    (text: string, searchTerm: string) => {
      if (!searchTerm.trim()) return text;

      const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const parts = text.split(new RegExp(`(${escapedTerm})`, "gi"));
      return parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span key={index} className="unified-search-highlight">
            {part}
          </span>
        ) : (
          part
        )
      );
    },
    []
  );

  const tabs: Array<{ key: TabType; label: string }> = [
    { key: "all", label: "All" },
    { key: "tags", label: "Tags" },
    { key: "content", label: "Content" },
    { key: "groups", label: "Groups" },
    { key: "people", label: "People" }
  ];

  const tags =
    activeTab === "all" || activeTab === "tags"
      ? searchResults?.data?.categories?.tags || []
      : [];

  const entities =
    activeTab === "all" || activeTab === "content"
      ? searchResults?.data?.categories?.libraryEntities || []
      : [];

  const groups =
    activeTab === "all" || activeTab === "groups"
      ? searchResults?.data?.categories?.groups || []
      : [];

  const people =
    activeTab === "all" || activeTab === "people"
      ? searchResults?.data?.categories?.people || []
      : [];

  const hasResults = useMemo(
    () =>
      searchResults?.data?.categories &&
      Object.values(searchResults.data.categories).some(
        category => category && category.length > 0
      ),
    [searchResults]
  );

  const isSingleCategory = useMemo(() => {
    if (!searchResults?.data?.categories) return false;

    const categories = searchResults.data.categories;
    const categoriesWithResults = [
      (categories.tags?.length ?? 0) > 0,
      (categories.libraryEntities?.length ?? 0) > 0,
      (categories.groups?.length ?? 0) > 0,
      (categories.people?.length ?? 0) > 0
    ].filter(Boolean);

    return categoriesWithResults.length === 1;
  }, [searchResults]);

  if (filteredView) {
    return (
      <FilteredPhotosPage
        entityType={filteredView.entityType}
        entityId={filteredView.entityId}
        entityName={filteredView.entityName}
        onBack={handleBack}
        selfieSearchS3Bucket={selfieSearchS3Bucket}
        currentUserId={currentUserId}
        redactionCheckEnabled={redactionCheckEnabled}
        canGenerateShowreels={canGenerateShowreels}
        organizationId={organizationId}
      />
    );
  }

  return (
    <div className="unified-search-results-page">
      {isLoading && (
        <div className="unified-search-loading">
          <img src={geoLoader} alt="Loading..." className="geo-loader" />
        </div>
      )}

      {!isLoading && query && !hasResults && (
        <div className="unified-search-no-results">
          <p>No results found for "{query}"</p>
        </div>
      )}

      {!isLoading && hasResults && (
        <>
          <div className="unified-search-results-header">
            <h1 className="unified-search-results-title">
              {getTotalCount()} results found for "
              <span className="unified-search-query">{query}</span>"
              <button
                type="button"
                className="unified-search-close"
                onClick={handleBack}
                aria-label="Close search results"
              >
                ×
              </button>
            </h1>
          </div>

          {!isSingleCategory && (
            <div className="unified-search-tabs">
              {tabs.map(tab => {
                const count = getTabCount(tab.key);
                if (count === 0 && tab.key !== "all") return null;

                return (
                  <button
                    key={tab.key}
                    className={`unified-search-tab ${
                      activeTab === tab.key ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <span className="unified-search-tab-count">{count}</span>
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="unified-search-results-content">
            <TagResults
              tags={tags}
              activeTab={activeTab}
              isSingleCategory={isSingleCategory}
              onTagClick={handleFilteredViewClick}
              setActiveTab={setActiveTab}
            />
            <ContentResults
              entities={entities}
              query={query}
              activeTab={activeTab}
              isSingleCategory={isSingleCategory}
              handleEntityClick={handleEntityClick}
              highlightSearchTerm={highlightSearchTerm}
              setActiveTab={setActiveTab}
            />
            {/* Groups feature not yet implemented - hidden until backend support is added */}
            {/* <GroupResults groups={groups} onGroupClick={handleFilteredViewClick} /> */}
            <PeopleResults
              people={people}
              query={query}
              activeTab={activeTab}
              isSingleCategory={isSingleCategory}
              highlightSearchTerm={highlightSearchTerm}
              onPersonClick={handleFilteredViewClick}
              setActiveTab={setActiveTab}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default UnifiedSearchResults;
